import moment from "moment";
import { isEqual } from "lodash";

import { USERS_COLL, PARAMETRIC_ANALYSIS_COLL, MODEL_COLL, firestore, fieldValue } from "_firebase";

import { checkForResults, resetEcmInputMapValidation } from "utils/parametric";
import { DEFAULT_ECM_INPUT_MAP } from "utils/parametric/constants";
import { seedInsightsParametricRuns } from "utils/payment/api";

import { provinceCodeMap } from "utils/weather";
import { emissionFactorsNIR } from "utils/emissions/getEmissionFactors";

import { change } from "store/root/actions";
import { unlockRunsWithLegacyCredits } from "utils/payment/api";

import {
    createParametricAnalysisStart,
    createParametricAnalysisSuccess,
    createParametricAnalysisError,
    createParametricAnalysisFolderError,
    createParametricAnalysisFolderStart,
    createParametricAnalysisFolderSuccess,
    deleteParametricAnalysisFolderError,
    deleteParametricAnalysisFolderSuccess,
    deleteParametricAnalysisFolderStart,
    deleteParametricAnalysisError,
    deleteParametricAnalysisSuccess,
    deleteParametricAnalysisStart,
    moveParametricAnalysisStart,
    moveParametricAnalysisSuccess,
    moveParametricAnalysisError,
    duplicateParametricAnalysisError,
    duplicateParametricAnalysisStart,
    duplicateParametricAnalysisSuccess,
    moveParametricAnalysisFolderStart,
    moveParametricAnalysisFolderSuccess,
    moveParametricAnalysisFolderError,
    renameParametricAnalysisFolderError,
    renameParametricAnalysisFolderStart,
    renameParametricAnalysisFolderSuccess,
    batchDeleteParametricAnalysisFoldersError,
    batchDeleteParametricAnalysisFoldersStart,
    batchDeleteParametricAnalysisFoldersSuccess,
    batchDeleteParametricAnalysisError,
    batchDeleteParametricAnalysisStart,
    batchDeleteParametricAnalysisSuccess,
    fetchParametricAnalysisError,
    fetchParametricAnalysisStart,
    fetchParametricAnalysisSuccess,
    syncParametricAnalysisError,
    syncParametricAnalysisStart,
    syncParametricAnalysisSuccess,
    saveEcmInputMapError,
    saveEcmInputMapStart,
    saveEcmInputMapSuccess,
    updateEcmInputMap,
    updateValidationPreCheckRunStatus,
    updateRunResults,
    pinUnpinRecipesError,
    pinUnpinRecipesStart,
    pinUnpinRecipesSuccess,
    updatePriceStateParametricStart,
    updatePriceStateParametricSuccess,
    updatePriceStateParametricError,
    updateEmissionFactorStateParametricStart,
    updateEmissionFactorStateParametricSuccess,
    updateEmissionFactorStateParametricError,
    saveParametricConfigurationStart,
    saveParametricConfigurationError,
    saveParametricConfigurationSuccess,
    saveParametricDetailsStart,
    saveParametricDetailsSuccess,
    saveParametricDetailsError,
    transferLegacyCreditsStart,
    transferLegacyCreditsSuccess,
    transferLegacyCreditsError,
    updateErsReferenceResults,
} from "./actions";

import { actions as userActions } from "store/users";
import { defaultUtilityCosts } from "utils/utilityData/defaultCosts";

const { fetchParametricDir, getUserParametricDir } = userActions;

// *************************************************
// PARAMETRIC ACTIONS
// *************************************************

export const createParametricAnalysis = (analysisToCreate, userUid) => async (dispatch) => {
    try {
        dispatch(createParametricAnalysisStart());

        const { analysisName, selectedModelId, currentFolderId, sourceAnalysisId } = analysisToCreate;

        const modelDetailsSnapshot = await firestore.doc(`${MODEL_COLL}/${selectedModelId}`).get();
        const modelDataSnapshot = await firestore
            .doc(`${MODEL_COLL}/${selectedModelId}`)
            .collection("modelData")
            .doc("data")
            .get();
        const upgradesSnapshot = await firestore
            .doc(`${MODEL_COLL}/${selectedModelId}`)
            .collection("modelData")
            .doc("upgrades")
            .get();

        const model = {
            modelDetails: modelDetailsSnapshot.data() || {},
            modelData: modelDataSnapshot.data() || {},
            upgrades: upgradesSnapshot.data() || {},
        };

        //Get default emission factors
        const { location: { weather: { region: { id: regionId } = {} } = {} } = {} } = model?.modelData || {};
        const provinceCode = provinceCodeMap[regionId];

        const {
            [provinceCode]: {
                elec: elecEfDefault = 0,
                ng: ngEfDefault = 0,
                propane: propaneEfDefault = 0,
                oil: oilEfDefault = 0,
                woodStove: woodEfDefault = 0,
            } = {},
        } = emissionFactorsNIR;

        const { hasResults = false, results: { base: { resultsData = {} } = {} } = {} } =
            (await checkForResults(model, selectedModelId)) || false;

        const allowedKeys = ["generalBaseCase", "socBaseCase", "ersReference"];
        const filteredResults = Object.keys(resultsData).reduce((acc, curr) => {
            if (allowedKeys.includes(curr)) {
                //Don't save hourly data
                const { hourlyElectricalLoadkWh = [], ...rest } = resultsData[curr];
                return { ...acc, [curr]: rest };
            }

            return acc;
        }, {});

        const { id } = await firestore.collection(PARAMETRIC_ANALYSIS_COLL).add({
            parametricDetails: {
                name: analysisName,
                selectedModelId,
                createdAt: new Date(),
                lastEdited: new Date(),
                lastModelSync: moment().format(),
                selectedModelHasResults: hasResults,
                owner: { uid: userUid },
                sharedWith: [userUid],
                lastValidationRun: { complete: null },
                lastErsRefRun: { complete: null },
                // availableRuns is always 0 on client create; Insights seed is server-only
                availableRuns: 0,
            },
        });

        // If the model has Insights and has not claimed its one-time 2500-run grant yet,
        // the backend seeds availableRuns and sets wasUsedInParametric.
        try {
            const seedResponse = await seedInsightsParametricRuns({
                analysisId: id,
                modelId: selectedModelId,
            });
            if (seedResponse?.isAxiosError) {
                console.log("Insights parametric run seed failed", seedResponse?.response?.data || seedResponse);
            }
        } catch (seedError) {
            console.log("Insights parametric run seed failed", seedError?.message || seedError);
        }

        await firestore
            .doc(`${PARAMETRIC_ANALYSIS_COLL}/${id}`)
            .collection("modelData")
            .doc("data")
            .set(model.modelData);

        await firestore
            .doc(`${PARAMETRIC_ANALYSIS_COLL}/${id}`)
            .collection("modelData")
            .doc("results")
            .set(filteredResults);

        await firestore
            .doc(`${PARAMETRIC_ANALYSIS_COLL}/${id}`)
            .collection("modelData")
            .doc("modelDetails")
            .set(model.modelDetails);

        await firestore
            .doc(`${PARAMETRIC_ANALYSIS_COLL}/${id}`)
            .collection("modelData")
            .doc("upgrades")
            .set(model.upgrades);

        await firestore
            .doc(`${USERS_COLL}/${userUid}`)
            .collection("parametricDir")
            .doc("directory")
            .set(
                {
                    analyses: {
                        [`${id}`]: {
                            name: analysisName,
                            lastEdited: moment().format(),
                            parentFolderId: currentFolderId,
                        },
                    },
                },
                { merge: true }
            );

        await firestore
            .doc(`${PARAMETRIC_ANALYSIS_COLL}/${id}`)
            .collection("config")
            .doc("configuration")
            .set({
                emissionFactors: {
                    electricity: elecEfDefault,
                    naturalGas: ngEfDefault,
                    oil: propaneEfDefault,
                    propane: oilEfDefault,
                    wood: woodEfDefault,
                },
                fuelCosts: defaultUtilityCosts,
            });

        if (sourceAnalysisId) {
            const ecmInputMapSnapshot = await firestore
                .doc(`${PARAMETRIC_ANALYSIS_COLL}/${sourceAnalysisId}`)
                .collection("config")
                .doc("ecmInputMap")
                .get();

            const sourceEcmInputMap = ecmInputMapSnapshot.exists
                ? ecmInputMapSnapshot.data()
                : { ...DEFAULT_ECM_INPUT_MAP };

            const ecmInputMap = resetEcmInputMapValidation(sourceEcmInputMap);

            await firestore
                .doc(`${PARAMETRIC_ANALYSIS_COLL}/${id}`)
                .collection("config")
                .doc("ecmInputMap")
                .set(ecmInputMap);
        }

        await dispatch(fetchParametricDir(userUid, true));

        dispatch(createParametricAnalysisSuccess());

        return id;
    } catch (error) {
        console.log("error", error.message);
        dispatch(createParametricAnalysisError(error));
    }
};

export const fetchParametricAnalysis = (analysisId) => async (dispatch, getState) => {
    try {
        dispatch(fetchParametricAnalysisStart());

        const analysisSnapshot = await firestore.doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`).get();

        if (!analysisSnapshot.exists) {
            throw new Error(JSON.stringify({ type: "not-found", message: "Analysis not found" }));
        }

        const analysisData = analysisSnapshot.data();
        const { user: { uid: userUid } = {} } = getState();

        // Legacy analyses never had an owner, assign the first user to open them.
        if (!analysisData?.parametricDetails?.owner && userUid) {
            const owner = { uid: userUid };
            await firestore.doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`).set(
                {
                    parametricDetails: { owner },
                },
                { merge: true }
            );
            analysisData.parametricDetails = {
                ...analysisData.parametricDetails,
                owner,
            };
        }

        const modelDataSnapshot = await firestore
            .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
            .collection("modelData")
            .doc("data")
            .get();

        const modelDetailsSnapshot = await firestore
            .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
            .collection("modelData")
            .doc("modelDetails")
            .get();

        const upgradesSnapshot = await firestore
            .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
            .collection("modelData")
            .doc("upgrades")
            .get();

        const resultsSnapshot = await firestore
            .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
            .collection("modelData")
            .doc("results")
            .get();

        const ecmInputMapSnapshot = await firestore
            .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
            .collection("config")
            .doc("ecmInputMap")
            .get();
        const configurationSnapshot = await firestore
            .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
            .collection("config")
            .doc("configuration")
            .get();

        const modelData = modelDataSnapshot.data();
        const modelDetails = modelDetailsSnapshot.data();
        const upgrades = upgradesSnapshot.data();
        const results = resultsSnapshot.data();

        const analysis = {
            ...analysisData,
            configuration: configurationSnapshot.exists ? configurationSnapshot.data() : {},
            model: { modelData, modelDetails: { ...modelDetails.modelDetails }, upgrades, results },
            ecmInputMap: ecmInputMapSnapshot.exists ? ecmInputMapSnapshot.data() : { ...DEFAULT_ECM_INPUT_MAP },
        };

        dispatch(fetchParametricAnalysisSuccess(analysis));
    } catch (error) {
        console.log("Analysis does not exist:", error.message);
        dispatch(fetchParametricAnalysisError(error.message));
    }
};

export const syncParametricAnalysis = (analysisId, selectedModelId) => async (dispatch, getState) => {
    try {
        dispatch(syncParametricAnalysisStart());

        const {
            parametricAnalysis: { parametricAnalysis: { ecmInputMap = {} } = {} },
        } = getState();

        const modelDetailsSnapshotFromModel = await firestore.doc(`${MODEL_COLL}/${selectedModelId}`).get();
        const modelDataSnapshotFromModel = await firestore
            .doc(`${MODEL_COLL}/${selectedModelId}`)
            .collection("modelData")
            .doc("data")
            .get();
        const upgradesSnapshotFromModel = await firestore
            .doc(`${MODEL_COLL}/${selectedModelId}`)
            .collection("modelData")
            .doc("upgrades")
            .get();

        const model = {
            modelDetails: modelDetailsSnapshotFromModel.data(),
            modelData: modelDataSnapshotFromModel.data(),
            upgrades: upgradesSnapshotFromModel.data(),
        };

        const { hasResults = false, results: { base: { resultsData = {} } = {} } = {} } =
            (await checkForResults(model, selectedModelId)) || false;

        const allowedKeys = ["generalBaseCase", "socBaseCase", "ersReference"];
        const filteredResults = Object.keys(resultsData).reduce((acc, curr) => {
            if (allowedKeys.includes(curr)) {
                //Don't save hourly data
                // eslint-disable-next-line no-unused-vars
                const { hourlyElectricalLoadkWh = [], ...rest } = resultsData[curr];
                return { ...acc, [curr]: rest };
            }

            return acc;
        }, {});
        // update analysis in parametric analysis collection
        await firestore.doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`).set(
            {
                parametricDetails: {
                    lastEdited: new Date(),
                    lastModelSync: moment().format(),
                    selectedModelHasResults: hasResults,
                },
            },
            { merge: true }
        );

        // update model data in modelData collection
        await firestore
            .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
            .collection("modelData")
            .doc("data")
            .set(model.modelData);

        await firestore
            .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
            .collection("modelData")
            .doc("results")
            .set(filteredResults);

        // update model details in modelData collection
        await firestore
            .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
            .collection("modelData")
            .doc("modelDetails")
            .set(model.modelDetails);

        // update upgrades in modelData collection
        await firestore
            .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
            .collection("modelData")
            .doc("upgrades")
            .set(model.upgrades);

        const analysisSnapshot = await firestore.doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`).get();
        const analysisData = analysisSnapshot.data();

        const modelDataSnapshot = await firestore
            .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
            .collection("modelData")
            .doc("data")
            .get();

        const modelDetailsSnapshot = await firestore
            .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
            .collection("modelData")
            .doc("modelDetails")
            .get();

        const upgradesSnapshot = await firestore
            .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
            .collection("modelData")
            .doc("upgrades")
            .get();

        const resultsSnapshot = await firestore
            .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
            .collection("modelData")
            .doc("results")
            .get();

        const modelData = modelDataSnapshot.data();
        const modelDetails = modelDetailsSnapshot.data();
        const upgrades = upgradesSnapshot.data();
        const results = resultsSnapshot.data();

        const analysis = {
            ...analysisData,
            ecmInputMap,
            model: { modelData, modelDetails: { ...modelDetails.modelDetails }, upgrades, results },
        };

        dispatch(syncParametricAnalysisSuccess({ ...analysis }));
    } catch (error) {
        console.log("error", error.message);
        dispatch(syncParametricAnalysisError(error.message));
    }
};

export const deleteParametricAnalysis = (analysisId, userUid) => async (dispatch) => {
    try {
        dispatch(deleteParametricAnalysisStart());

        await firestore.doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`).delete();

        await firestore
            .doc(`${USERS_COLL}/${userUid}`)
            .collection("parametricDir")
            .doc("directory")
            .update({
                [`analyses.${analysisId}`]: fieldValue.delete(),
            });

        await dispatch(fetchParametricDir(userUid, true));

        dispatch(deleteParametricAnalysisSuccess());

        return;
    } catch (error) {
        console.log("error", error.message);
        dispatch(deleteParametricAnalysisError(error));
    }
};

export const moveParametricAnalysis = (analysisId, userUid, moveToId) => async (dispatch) => {
    try {
        dispatch(moveParametricAnalysisStart());

        await firestore
            .doc(`${USERS_COLL}/${userUid}`)
            .collection("parametricDir")
            .doc("directory")
            .update({
                [`analyses.${analysisId}.parentFolderId`]: moveToId,
            });

        await dispatch(fetchParametricDir(userUid, true));

        dispatch(moveParametricAnalysisSuccess());
    } catch (error) {
        console.log("error", error.message);
        dispatch(moveParametricAnalysisError(error));
    }
};

// itemToDuplicate, uid, currentFolderId, duplicateName
export const duplicateParametricAnalysis =
    (analysisId, userUid, currentFolderId, duplicateName) => async (dispatch) => {
        try {
            dispatch(duplicateParametricAnalysisStart());

            const analysisSnapshot = await firestore.doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`).get();

            const analysisData = analysisSnapshot.data();

            const modelDataSnapshot = await firestore
                .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
                .collection("modelData")
                .doc("data")
                .get();
            const modelDetailsSnapshot = await firestore
                .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
                .collection("modelData")
                .doc("modelDetails")
                .get();
            const upgradesSnapshot = await firestore
                .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
                .collection("modelData")
                .doc("upgrades")
                .get();

            const modelData = modelDataSnapshot.data();
            const modelDetails = modelDetailsSnapshot.data();
            const upgrades = upgradesSnapshot.data();

            const { id } = await firestore.collection(PARAMETRIC_ANALYSIS_COLL).add({
                parametricDetails: {
                    name: duplicateName,
                    selectedModelId: analysisData.parametricDetails.selectedModelId,
                    createdAt: new Date(),
                    lastEdited: new Date(),
                    lastModelSync: moment().format(),
                    owner: { uid: userUid },
                    sharedWith: [userUid],
                    availableRuns: 0,
                },
            });

            await firestore.doc(`${PARAMETRIC_ANALYSIS_COLL}/${id}`).collection("modelData").doc("data").set(modelData);

            await firestore
                .doc(`${PARAMETRIC_ANALYSIS_COLL}/${id}`)
                .collection("modelData")
                .doc("modelDetails")
                .set(modelDetails);

            await firestore
                .doc(`${PARAMETRIC_ANALYSIS_COLL}/${id}`)
                .collection("modelData")
                .doc("upgrades")
                .set(upgrades);

            await firestore
                .doc(`${USERS_COLL}/${userUid}`)
                .collection("parametricDir")
                .doc("directory")
                .update({
                    [`analyses.${id}`]: {
                        name: duplicateName,
                        lastEdited: moment().format(),
                        parentFolderId: currentFolderId,
                    },
                });

            await dispatch(fetchParametricDir(userUid, true));

            dispatch(duplicateParametricAnalysisSuccess());
        } catch (error) {
            console.log("error", error.message);
            dispatch(duplicateParametricAnalysisError(error));
        }
    };

export const batchDeleteParametricAnalysis = (analysesIds, userUid) => async (dispatch) => {
    try {
        dispatch(batchDeleteParametricAnalysisStart());

        for await (const id of analysesIds) {
            await dispatch(deleteParametricAnalysis(id, userUid));
        }

        dispatch(batchDeleteParametricAnalysisSuccess());
    } catch (error) {
        console.log("error", error.message);
        dispatch(batchDeleteParametricAnalysisError(error));
    }
};

export const saveEcmInputMap =
    (analysisId, isFromError = false) =>
    async (dispatch, getState) => {
        try {
            dispatch(saveEcmInputMapStart());

            const { parametricAnalysis: { parametricAnalysis: { ecmInputMap } = {} } = {} } = getState();

            await firestore
                .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
                .collection("config")
                .doc("ecmInputMap")
                .set(ecmInputMap);

            if (!isFromError) dispatch(saveEcmInputMapSuccess());
        } catch (error) {
            console.log("error", error.message);
            dispatch(saveEcmInputMapError(error));
        }
    };

export const saveParametricConfiguration =
    (analysisId, isFromError = false) =>
    async (dispatch, getState) => {
        try {
            if (!isFromError) dispatch(saveParametricConfigurationStart());

            const {
                parametricAnalysis: {
                    parametricAnalysis: { configuration = {} } = {},
                    parametricAnalysisToCompare: { configuration: configurationToCompare = {} } = {},
                } = {},
            } = getState();

            const { fuelCosts = {}, emissionFactors = {} } = configuration || {};
            const { fuelCosts: fuelCostsToCompare = {}, emissionFactors: emissionFactorsToCompare = {} } =
                configurationToCompare || {};

            if (!isEqual(fuelCosts, fuelCostsToCompare) && !isFromError)
                await dispatch(updatePriceStateParametric(analysisId));
            if (!isEqual(emissionFactors, emissionFactorsToCompare) && !isFromError)
                await dispatch(updateEmissionFactorStateParametric(analysisId));

            await firestore
                .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
                .collection("config")
                .doc("configuration")
                .set(configuration, { merge: true });

            if (!isFromError) dispatch(saveParametricConfigurationSuccess(configuration));
        } catch (error) {
            console.log("error", error.message);
            dispatch(saveParametricConfigurationError(error));
        }
    };

export const saveParametricDetails =
    (analysisId, isFormError = false) =>
    async (dispatch, getState) => {
        try {
            if (!isFormError) dispatch(saveParametricDetailsStart());

            const { parametricAnalysis: { parametricAnalysis: { parametricDetails = {} } = {} } = {} } = getState();

            const { availableRuns, runHistory, lastValidationRun, totalCost, lastErsRefRun, ...editableDetails } =
                parametricDetails;

            firestore.doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`).set(
                {
                    parametricDetails: editableDetails,
                },
                { merge: true }
            );

            if (!isFormError) dispatch(saveParametricDetailsSuccess(parametricDetails));
        } catch (error) {
            console.log("error saving parametric details", error.message);
            dispatch(saveParametricDetailsError(error));
        }
    };

export const pinUnpinRecipes = (analysisId, recipeId) => (dispatch, getState) => {
    try {
        dispatch(pinUnpinRecipesStart());

        const { parametricAnalysis: { parametricDetails: { pinnedRecipes = [] } } = {} } =
            getState().parametricAnalysis;

        let updatedPinnedRecipes = [...pinnedRecipes];

        const allIds = updatedPinnedRecipes.map(({ id }) => id);

        const isAdding = !allIds.includes(recipeId);

        if (isAdding) {
            const highestOrder = updatedPinnedRecipes.reduce((acc, { order }) => (order > acc ? order : acc), 0);

            updatedPinnedRecipes = [
                ...updatedPinnedRecipes,
                {
                    id: recipeId,
                    label: `Pinned ${updatedPinnedRecipes.length + 1}`,
                    order: highestOrder + 1,
                },
            ];
        }

        if (!isAdding) {
            updatedPinnedRecipes = updatedPinnedRecipes.filter(({ id }) => id !== recipeId);
        }

        // Updating order to  be sequential
        updatedPinnedRecipes = updatedPinnedRecipes
            .sort((a, b) => a.order - b.order)
            .map((recipe, index) => ({
                ...recipe,
                order: index,
            }));

        firestore.doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`).set(
            {
                parametricDetails: {
                    pinnedRecipes: updatedPinnedRecipes,
                },
            },
            { merge: true }
        );

        dispatch(pinUnpinRecipesSuccess(updatedPinnedRecipes));
    } catch (error) {
        console.log("error", error.message);
        dispatch(pinUnpinRecipesError(error));
    }
};

export const updatePriceStateParametric = (analysisId) => async (dispatch) => {
    try {
        dispatch(updatePriceStateParametricStart());

        await firestore.doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`).set(
            {
                parametricDetails: {
                    totalCost: {
                        wasTotalCostChanged: true,
                    },
                },
            },
            { merge: true }
        );

        dispatch(updatePriceStateParametricSuccess());
    } catch (error) {
        dispatch(updatePriceStateParametricError(error));
    }
};

export const updateEmissionFactorStateParametric = (analysisId) => async (dispatch) => {
    try {
        dispatch(updateEmissionFactorStateParametricStart());

        await firestore.doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`).set(
            {
                parametricDetails: {
                    emissionFactor: {
                        wasEmissionFactorChanged: true,
                    },
                },
            },
            { merge: true }
        );

        dispatch(updateEmissionFactorStateParametricSuccess());
    } catch (error) {
        dispatch(updateEmissionFactorStateParametricError(error));
    }
};

export const watchParametricUpdates = (analysisId) => (dispatch) => {
    const unsubConfig = firestore
        .doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`)
        .collection("config")
        .doc("ecmInputMap")
        .onSnapshot((doc) => {
            dispatch(updateEcmInputMap(doc.data()));
        });

    const unsubDetails = firestore.doc(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}`).onSnapshot((doc) => {
        const {
            parametricDetails: { lastValidationRun = {}, runHistory = {}, totalCost = {}, availableRuns = 0 } = {},
        } = doc.data() || {};
        dispatch(updateValidationPreCheckRunStatus({ lastValidationRun, runHistory, totalCost, availableRuns }));
    });

    const unsubErsRefs = firestore
        .collection(`${PARAMETRIC_ANALYSIS_COLL}/${analysisId}/ersReferenceResults`)
        .onSnapshot((snapshot) => {
            const docs = {};
            snapshot.docs.forEach((doc) => {
                docs[doc.id] = doc.data();
            });
            dispatch(updateErsReferenceResults(docs));
        });

    return () => {
        unsubConfig();
        unsubDetails();
        unsubErsRefs();
    };
};

// *************************************************
// FOLDERS ACTIONS
// *************************************************

export const createParametricAnalysisFolder = (folderName, userUid, currentFolderId) => async (dispatch) => {
    try {
        dispatch(createParametricAnalysisFolderStart());

        const folderId = firestore.doc(`${USERS_COLL}/${userUid}`).collection("parametricDir").doc().id;

        await firestore
            .doc(`${USERS_COLL}/${userUid}`)
            .collection("parametricDir")
            .doc("directory")
            .set(
                {
                    folders: {
                        [`${folderId}`]: {
                            name: folderName,
                            lastEdited: moment().format(),
                            parentFolderId: currentFolderId,
                        },
                    },
                },
                { merge: true }
            );

        await dispatch(fetchParametricDir(userUid, true));

        dispatch(createParametricAnalysisFolderSuccess());
    } catch (error) {
        console.log("error", error.message);
        dispatch(createParametricAnalysisFolderError(error));
    }
};

export const deleteParametricAnalysisFolder = (folderId, userUid) => async (dispatch) => {
    try {
        dispatch(deleteParametricAnalysisFolderStart());

        const analysesDir = (await getUserParametricDir(userUid)).data();

        const folders = analysesDir.folders;
        const analyses = analysesDir.analyses;

        const foldersIds =
            Object.entries(folders)
                .filter(([key, { parentFolderId }]) => parentFolderId === folderId)
                .map(([key]) => key) || [];
        const analysesIds =
            Object.entries(analyses)
                .filter(([key, { parentFolderId }]) => parentFolderId === folderId)
                .map(([key]) => key) || [];

        if (foldersIds.length > 0) {
            for await (const id of foldersIds) {
                await firestore
                    .doc(`${USERS_COLL}/${userUid}`)
                    .collection("parametricDir")
                    .doc("directory")
                    .update({
                        [`folders.${id}.parentFolderId`]: null,
                    });
            }
        }

        if (analysesIds.length > 0) {
            for await (const id of analysesIds) {
                await firestore
                    .doc(`${USERS_COLL}/${userUid}`)
                    .collection("parametricDir")
                    .doc("directory")
                    .update({
                        [`analyses.${id}.parentFolderId`]: null,
                    });
            }
        }

        await firestore
            .doc(`${USERS_COLL}/${userUid}`)
            .collection("parametricDir")
            .doc("directory")
            .update({
                [`folders.${folderId}`]: fieldValue.delete(),
            });

        await dispatch(fetchParametricDir(userUid, true));

        dispatch(deleteParametricAnalysisFolderSuccess());
    } catch (error) {
        console.log("error", error.message);
        dispatch(deleteParametricAnalysisFolderError(error));
    }
};

export const moveParametricAnalysisFolder = (folderId, userUid, moveToId) => async (dispatch) => {
    try {
        dispatch(moveParametricAnalysisFolderStart());

        await firestore
            .doc(`${USERS_COLL}/${userUid}`)
            .collection("parametricDir")
            .doc("directory")
            .update({
                [`folders.${folderId}.parentFolderId`]: moveToId,
            });

        await dispatch(fetchParametricDir(userUid, true));

        dispatch(moveParametricAnalysisFolderSuccess());
    } catch (error) {
        console.log("error", error.message);
        dispatch(moveParametricAnalysisFolderError(error));
    }
};

export const renameParametricAnalysisFolder = (folderId, userUid, newName) => async (dispatch) => {
    try {
        dispatch(renameParametricAnalysisFolderStart());

        await firestore
            .doc(`${USERS_COLL}/${userUid}`)
            .collection("parametricDir")
            .doc("directory")
            .update({
                [`folders.${folderId}.name`]: newName,
            });

        await dispatch(fetchParametricDir(userUid, true));

        dispatch(renameParametricAnalysisFolderSuccess());
    } catch (error) {
        console.log("error", error.message);
        dispatch(renameParametricAnalysisFolderError(error));
    }
};

export const batchDeleteParametricAnalysisFolders = (foldersIds, userUid) => async (dispatch) => {
    try {
        dispatch(batchDeleteParametricAnalysisFoldersStart());

        for await (const id of foldersIds) {
            await dispatch(deleteParametricAnalysisFolder(id, userUid));
        }

        dispatch(batchDeleteParametricAnalysisFoldersSuccess());
    } catch (error) {
        console.log("error", error.message);
        dispatch(batchDeleteParametricAnalysisFoldersError(error));
    }
};

export const transferLegacyCredits = (analysisId, _userUid, creditsToTransfer) => async (dispatch) => {
    try {
        const creditAmount = Number(creditsToTransfer);
        if (!Number.isInteger(creditAmount) || creditAmount <= 0) {
            throw new Error("Enter a whole number of legacy credits");
        }

        if (!analysisId) {
            throw new Error("Missing parametric analysis id");
        }

        dispatch(transferLegacyCreditsStart());

        const response = await unlockRunsWithLegacyCredits({
            analysisId,
            credits: creditAmount,
        });

        if (!response?.data || response?.isAxiosError) {
            const payload = response?.response?.data || {};
            const message = payload?.message || response?.message || "Failed to unlock runs with legacy credits";

            if (payload?.creditSummary?.total != null) {
                dispatch(change("user.creditSummary.total", payload.creditSummary.total));
            }

            throw Object.assign(new Error(message), { payload });
        }

        const {
            creditSummary: { total: newLegacyTotalCredits = 0, credits: newUserCredits } = {},
            availableRuns: newAvailableRuns,
        } = response.data;

        dispatch(change("user.creditSummary.total", newLegacyTotalCredits));
        if (newUserCredits != null) {
            dispatch(change("user.creditSummary.credits", newUserCredits));
        }

        if (newAvailableRuns != null) {
            dispatch(change("parametricAnalysis.parametricAnalysis.parametricDetails.availableRuns", newAvailableRuns));
            dispatch(
                change(
                    "parametricAnalysis.parametricAnalysisToCompare.parametricDetails.availableRuns",
                    newAvailableRuns
                )
            );
        }

        dispatch(transferLegacyCreditsSuccess(newLegacyTotalCredits, newAvailableRuns));
        return response.data;
    } catch (error) {
        console.log("error", error.message);
        dispatch(transferLegacyCreditsError(error));
        throw error;
    }
};
